// context/CartContext.tsx
import React, { createContext, useEffect, useState } from 'react';
import type { CartItem } from '../../../shared/types/cart';
import type { CouponEntity } from '../../../shared/types/coupon.entity';
import { CART_STORAGE_KEY } from '../constants/cart.constants';
import type { Cart } from '../types/cart';
import { isArrayEqual } from '../utils/array.utils';
import { getCart } from '../utils/cart.utils';

type CartContextType = {
	cart: Cart;
	addItem: (item: CartItem) => void;
	removeItem: (id: string) => void;
	clearCart: () => void;
	updateItemQuantity: (id: string, quantity: number) => void;
	updateCartCouponCode: (coupon: CouponEntity | undefined) => void;
	setCartItems: (items: CartItem[]) => void;
};

export const CartContext = createContext<CartContextType | undefined>(
	undefined
);

function normalizeAddOns(
	addOns: CartItem['selectedAddOns']
): NonNullable<CartItem['selectedAddOns']> {
	return [...(addOns ?? [])]
		.map((a) => ({
			selectedAddOnId: a.selectedAddOnId,
			selectedOptionIds: [...a.selectedOptionIds].sort(),
			quantities: { ...(a.quantities ?? {}) },
		}))
		.sort((a, b) => a.selectedAddOnId.localeCompare(b.selectedAddOnId));
}

function areCartItemsEqual(a: CartItem, b: CartItem): boolean {
	if (a.product.id !== b.product.id) return false;
	if (a.selectedVariants?.length !== b.selectedVariants?.length) return false;

	if (a.selectedVariants && b.selectedVariants) {
		const sortedA = [...a.selectedVariants].sort((a, b) =>
			a.optionId.localeCompare(b.optionId)
		);
		const sortedB = [...b.selectedVariants].sort((a, b) =>
			a.optionId.localeCompare(b.optionId)
		);
		for (let i = 0; i < sortedA.length; i++) {
			if (
				sortedA[i].optionId !== sortedB[i].optionId ||
				sortedA[i].variantId !== sortedB[i].variantId
			)
				return false;
		}
	} else if (a.selectedVariants || b.selectedVariants) {
		return false;
	}

	const addOnsA = normalizeAddOns(a.selectedAddOns ?? []);
	const addOnsB = normalizeAddOns(b.selectedAddOns ?? []);

	if (addOnsA.length !== addOnsB.length) return false;

	return addOnsA.every((addOn, i) => {
		return (
			addOn.selectedAddOnId === addOnsB[i].selectedAddOnId &&
			isArrayEqual(addOn.selectedOptionIds, addOnsB[i].selectedOptionIds)
		);
	});
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [cart, setCart] = useState<Cart>(getCart());

	useEffect(() => {
		localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
	}, [cart]);

	const addItem = (item: CartItem) => {
		setCart((prev) => {
			const existing = prev.items.find((i) => i.id === item.id);
			const exactProduct = prev.items.find((i) =>
				areCartItemsEqual(i, item)
			);
			let items: CartItem[] = [];
			if (existing) {
				const anotherExactProduct = prev.items.find(
					(i) => i.id !== item.id && areCartItemsEqual(i, item)
				);
				if (anotherExactProduct) {
					items = prev.items
						.filter((i) => i.id !== anotherExactProduct.id)
						.map((i) =>
							i.id === item.id
								? {
										...item,
										quantity:
											(item.quantity ?? 0) +
											(anotherExactProduct.quantity ?? 0),
									}
								: i
						);
				} else {
					items = prev.items.map((i) =>
						i.id === item.id ? item : i
					);
				}
			} else if (exactProduct) {
				items = [
					...prev.items.map((i) =>
						areCartItemsEqual(i, item)
							? {
									...i,
									quantity:
										(i.quantity ?? 0) +
										(item.quantity ?? 0),
								}
							: i
					),
				];
			} else {
				items = [...prev.items, item];
			}
			const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
			return { ...prev, items, totalQuantity };
		});
	};

	const removeItem = (id: string) => {
		setCart((prev) => {
			const items = prev.items.filter((i) => i.id !== id);
			const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
			return { ...prev, items, totalQuantity };
		});
	};

	const clearCart = () => {
		setCart({ items: [] });
	};

	const updateItemQuantity = (id: string, quantity: number) => {
		setCart((prev) => {
			if (prev.items.length === 0) return prev;
			let items = prev.items.map((item) => {
				if (item.id !== id) return item;
				return { ...item, quantity };
			});
			if (quantity <= 0) {
				items = items.filter((item) => item.id !== id);
			}
			return { ...prev, items };
		});
	};

	const updateCartCouponCode = (coupon: CouponEntity | undefined) => {
		setCart((prev) => ({
			...prev,
			coupon,
		}));
	};

	const setCartItems = (items: CartItem[]) => {
		setCart((prev) => {
			const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
			return { ...prev, items, totalQuantity };
		});
	};

	return (
		<CartContext.Provider
			value={{
				cart,
				addItem,
				removeItem,
				clearCart,
				updateItemQuantity,
				updateCartCouponCode,
				setCartItems,
			}}
		>
			{children}
		</CartContext.Provider>
	);
};
